import React, { useState, useEffect, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Spinner,
  Table,
  Alert,
  Badge,
  Breadcrumb,
  Accordion,
} from "react-bootstrap";
import { useAuth } from "@clerk/clerk-react";
import { debounce } from "lodash";
import { Link } from "react-router-dom";
import { Download } from "react-bootstrap-icons";
import createAxiosInstance from "../utility/utils";

const BreadcrumbNav = () => (
  <Breadcrumb className="mb-4">
    <Breadcrumb.Item href="/">Apps</Breadcrumb.Item>
    <Breadcrumb.Item active>Attendance Report</Breadcrumb.Item>
  </Breadcrumb>
);

const SubjectAttendanceCard = ({ subject, data }) => {
  if (!data || !data.stats) return null;

  return (
    <Card className="h-100">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <h6 className="mb-0">{subject}</h6>
        <Badge bg={Number(data.stats.percentage) >= 75 ? "success" : "danger"}>
          {data.stats.percentage.toFixed(1)}% Attendance
        </Badge>
      </Card.Header>
      <Card.Body>
        <Table striped bordered hover size="sm" className="mb-0">
          <tbody>
            <tr>
              <td>Total Classes</td>
              <td className="text-end">{data.stats.total}</td>
            </tr>
            <tr>
              <td>Present</td>
              <td className="text-end">{data.stats.present}</td>
            </tr>
            <tr>
              <td>Absent</td>
              <td className="text-end">
                {data.stats.total - data.stats.present}
              </td>
            </tr>
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
};

const StudentCard = ({
  student,
  downloadingId,
  setDownloadingId,
  dateRange,
  setError,
}) => {
  if (!student || !student.subjects) return null;

  // Calculate overall attendance
  const getOverallAttendance = () => {
    let totalPresent = 0;
    let totalClasses = 0;

    Object.values(student.subjects).forEach((subject) => {
      if (subject.stats) {
        totalPresent += subject.stats.present;
        totalClasses += subject.stats.total;
      }
    });

    return totalClasses > 0
      ? ((totalPresent / totalClasses) * 100).toFixed(1)
      : 0;
  };

  const overallAttendance = getOverallAttendance();
  const attendanceVariant =
    Number(overallAttendance) >= 75 ? "success" : "danger";
  const isDownloading = downloadingId === student._id;

  const generateCsvContent = () => {
    let csvContent =
      "Student Name,USN Number,Email,Subject,Total Classes,Present,Absent,Attendance Percentage\n";

    Object.entries(student.subjects).forEach(([subject, data]) => {
      if (data.stats) {
        const present = data.stats.present;
        const total = data.stats.total;
        const absent = total - present;
        const percentage = data.stats.percentage.toFixed(1);

        csvContent += `${student.name},${student.usnNumber},${student.email},${subject},${total},${present},${absent},${percentage}%\n`;
      }
    });

    let totalPresent = 0;
    let totalClasses = 0;

    Object.values(student.subjects).forEach((subject) => {
      if (subject.stats) {
        totalPresent += subject.stats.present;
        totalClasses += subject.stats.total;
      }
    });

    const totalAbsent = totalClasses - totalPresent;
    const totalPercentage = getOverallAttendance();

    csvContent += `${student.name},${student.usnNumber},${student.email},"OVERALL",${totalClasses},${totalPresent},${totalAbsent},${totalPercentage}%\n`;

    return csvContent;
  };

  const handleStudentDownload = async () => {
    if (downloadingId) return;

    setDownloadingId(student._id);
    try {
      const csvContent = generateCsvContent();

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);

      const dateStr =
        dateRange.startDate && dateRange.endDate
          ? `${dateRange.startDate}_to_${dateRange.endDate}`
          : new Date().toISOString().split("T")[0];

      const filename = `attendance_${student.name.replace(/\s+/g, "_")}_${
        student.usnNumber
      }_${dateStr}.csv`;

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setError("Failed to download student report. Please try again.");
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <Accordion.Item eventKey={student._id} className="mb-3 shadow-sm border-0">
      <Accordion.Header className="py-2">
        <div className="d-flex justify-content-between align-items-center w-100 me-3">
          <div>
            <h5 className="mb-0 d-flex align-items-center">
              {student.name}
              <Badge bg={attendanceVariant} className="ms-2">
                {overallAttendance}%
              </Badge>
            </h5>
            <small className="text-muted d-flex align-items-center mt-1">
              <span className="me-3">USN: {student.usnNumber}</span>
              <span>Email: {student.email}</span>
            </small>
          </div>
        </div>
      </Accordion.Header>
      <Accordion.Body className="bg-light">
        <div className="mb-3">
          <Button
            variant="outline-primary"
            size="sm"
            className="d-flex align-items-center"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleStudentDownload();
            }}
            disabled={isDownloading}
          >
            {isDownloading ? (
              <>
                <Spinner size="sm" className="me-1" />
                <span>Downloading...</span>
              </>
            ) : (
              <>
                <Download className="me-1" />
                <span>Download Report</span>
              </>
            )}
          </Button>
        </div>
        <Row className="g-3">
          {Object.entries(student.subjects).map(([subject, data]) => (
            <Col md={6} lg={4} key={subject}>
              <SubjectAttendanceCard subject={subject} data={data} />
            </Col>
          ))}
        </Row>
      </Accordion.Body>
    </Accordion.Item>
  );
};

const FilterCard = ({
  dateRange,
  setDateRange,
  onSearch,
  onMasterDownload,
  downloadingId,
  validateDates,
}) => {
  const handleDateChange = (field, value) => {
    setDateRange((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <Card className="mb-4 shadow">
      <Card.Body>
        <Row className="g-3">
          <Col md={3}>
            <Form.Group>
              <Form.Label>Search Students</Form.Label>
              <Form.Control
                type="text"
                placeholder="Name, USN, or Email"
                onChange={(e) => onSearch(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Start Date</Form.Label>
              <Form.Control
                type="date"
                name="startDate"
                value={dateRange.startDate}
                onChange={(e) => handleDateChange("startDate", e.target.value)}
                max={dateRange.endDate || undefined}
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>End Date</Form.Label>
              <Form.Control
                type="date"
                name="endDate"
                value={dateRange.endDate}
                onChange={(e) => handleDateChange("endDate", e.target.value)}
                min={dateRange.startDate || undefined}
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Button
              variant="success"
              className="w-100 mt-4"
              onClick={onMasterDownload}
              disabled={downloadingId !== null || !validateDates()}
            >
              {downloadingId === "master" ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Downloading...
                </>
              ) : (
                "Download Complete Report"
              )}
            </Button>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

const MasterAttendanceView = () => {
  const { getToken } = useAuth();
  const axiosInstance = useMemo(
    () => createAxiosInstance(getToken),
    [getToken]
  );

  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [dateRange, setDateRange] = useState({
    startDate: "",
    endDate: "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalStudents, setTotalStudents] = useState(0);
  const studentsPerPage = 20;

  const fetchStudentData = async (page = 1, isLoadMore = false) => {
    if (loading) return;

    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: studentsPerPage.toString(),
        ...(searchQuery && { searchQuery: searchQuery.trim() }),
      });

      if (dateRange.startDate && dateRange.endDate) {
        const startDate = new Date(dateRange.startDate);
        startDate.setHours(0, 0, 0, 0);

        const endDate = new Date(dateRange.endDate);
        endDate.setHours(23, 59, 59, 999);

        params.append("startDate", startDate.toISOString());
        params.append("endDate", endDate.toISOString());
      }

      const response = await axiosInstance.get(
        `/api/attendance/comprehensive?${params}`
      );
      const { attendance, pagination } = response.data;

      if (!attendance || !Array.isArray(attendance)) {
        throw new Error("Invalid attendance data received");
      }

      setTotalStudents(pagination?.total || 0);
      setStudents((prev) =>
        isLoadMore ? [...prev, ...attendance] : attendance
      );
      setHasMore(attendance.length === studentsPerPage);
      setCurrentPage(page);
    } catch (error) {
      setError(
        error.response?.data?.error ||
          "Failed to fetch attendance data. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setStudents([]);
    setCurrentPage(1);
    setHasMore(true);
    fetchStudentData(1, false);
  }, [searchQuery, dateRange.startDate, dateRange.endDate]);

  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        setSearchQuery(value.trim());
      }, 500),
    []
  );

  const handleMasterDownload = async () => {
    if (downloadingId) return;

    setDownloadingId("master");
    try {
      const params = new URLSearchParams();

      if (dateRange.startDate && dateRange.endDate) {
        const startDate = new Date(dateRange.startDate);
        startDate.setHours(0, 0, 0, 0);

        const endDate = new Date(dateRange.endDate);
        endDate.setHours(23, 59, 59, 999);

        params.append("startDate", startDate.toISOString());
        params.append("endDate", endDate.toISOString());
      }

      const response = await axiosInstance.get(
        `/api/attendance/download-report?${params}`,
        { responseType: "blob" }
      );

      if (!(response.data instanceof Blob)) {
        throw new Error("Invalid response format");
      }

      const dateStr =
        dateRange.startDate && dateRange.endDate
          ? `${dateRange.startDate}_to_${dateRange.endDate}`
          : new Date().toISOString().split("T")[0];

      const filename = `attendance_master_report_${dateStr}.csv`;

      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setError("Failed to download master report. Please try again.");
    } finally {
      setDownloadingId(null);
    }
  };

  const validateDates = () => {
    if (dateRange.startDate && dateRange.endDate) {
      const start = new Date(dateRange.startDate);
      const end = new Date(dateRange.endDate);
      return start <= end;
    }
    return true;
  };

  return (
    <Container fluid className="py-4">
      <BreadcrumbNav />

      <FilterCard
        dateRange={dateRange}
        setDateRange={setDateRange}
        onSearch={debouncedSearch}
        onMasterDownload={handleMasterDownload}
        downloadingId={downloadingId}
        validateDates={validateDates}
      />

      {error && (
        <Alert variant="danger" onClose={() => setError("")} dismissible>
          {error}
        </Alert>
      )}

      <div className="mb-3 d-flex justify-content-between align-items-center">
        <strong>Total Students: {totalStudents}</strong>
        {!validateDates() && (
          <Alert variant="warning" className="mb-0 py-1">
            End date must be after start date
          </Alert>
        )}
      </div>

      {loading && !students.length ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : (
        <>
          <Accordion>
            {students.map((student) => (
              <StudentCard
                key={student._id}
                student={student}
                downloadingId={downloadingId}
                setDownloadingId={setDownloadingId}
                dateRange={dateRange}
                setError={setError}
              />
            ))}
          </Accordion>

          {hasMore && (
            <div className="text-center mt-4">
              <Button
                variant="primary"
                onClick={() => fetchStudentData(currentPage + 1, true)}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Loading...
                  </>
                ) : (
                  "Load More"
                )}
              </Button>
            </div>
          )}

          {!loading && students.length === 0 && (
            <Alert variant="info">
              No students found matching the search criteria
            </Alert>
          )}
        </>
      )}
    </Container>
  );
};

export default MasterAttendanceView;
